import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Chip, CircularProgress,
  Stack, Button, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Calculate as CalculateIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import { payrollApi } from '../../../api/payrollApi';
import {
  PayCycle,
  PayCycleLabels,
  PayrollRunDetailDto,
  PayrollRunStatus,
  PayrollRunStatusLabels,
  PayrollRunStatusColors,
  SalaryComponentType,
  PayrollComponentDetailDto,
  PayrollAdjustmentType,
  PayrollAdjustmentTypeLabels,
  PayrollAdjustmentDto,
  PayrollEmployeeDetailDto,
} from '../../../types/payrollTypes';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-IN');
};

const getRunPeriodLabel = (detail: PayrollRunDetailDto) => {
  if ((detail.payCycle ?? PayCycle.Monthly) === PayCycle.Weekly) {
    return `${formatDate(detail.periodStart)} - ${formatDate(detail.periodEnd)}`;
  }

  return `${MONTHS[detail.month - 1]} ${detail.year}`;
};

const deductionComponentTypes = [
  SalaryComponentType.Deduction,
  SalaryComponentType.TaxComponent,
  SalaryComponentType.LeaveComponent,
];

// LOP / Advance adjustments reduce pay, so the recalculation applies them on the deduction side -
// they subtract from the Adjustments total instead of adding to it.
const isDeductionAdjustment = (type: PayrollAdjustmentType) =>
  type === PayrollAdjustmentType.LopAdjustment || type === PayrollAdjustmentType.AdvanceDeduction;

// An Overtime adjustment is folded into OvertimeAmount by the recalculation, so it is already part
// of the OT Amount column (and of the single OT Amount line on the payslip). Counting it here too
// would show the same money twice on this row, which is not what Total Earnings did with it.
const isOvertimeAdjustment = (type: PayrollAdjustmentType) => type === PayrollAdjustmentType.Overtime;

const adjustmentSignedAmount = (adjustment: PayrollAdjustmentDto) =>
  (isDeductionAdjustment(adjustment.adjustmentType) ? -adjustment.amount : adjustment.amount);

const netAdjustmentTotal = (adjustments: PayrollAdjustmentDto[]) => adjustments
  .filter((adjustment) => !isOvertimeAdjustment(adjustment.adjustmentType))
  .reduce((sum, adjustment) => sum + adjustmentSignedAmount(adjustment), 0);

const formatDays = (value?: number | null) => (value ?? 0).toLocaleString('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const formatMoney = (value?: number | null) => `Rs. ${(value ?? 0).toLocaleString('en-IN')}`;

const PayrollRunDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<PayrollRunDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [adjustmentEmployee, setAdjustmentEmployee] = useState<PayrollEmployeeDetailDto | null>(null);
  const [adjustmentType, setAdjustmentType] = useState<PayrollAdjustmentType>(PayrollAdjustmentType.Bonus);
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentRemarks, setAdjustmentRemarks] = useState('');
  const [savingAdjustment, setSavingAdjustment] = useState(false);
  const [bankAdviceEnabled, setBankAdviceEnabled] = useState(false);
  const [downloadingBankAdvice, setDownloadingBankAdvice] = useState(false);
  const [workflowAction, setWorkflowAction] = useState<string | null>(null);
  const [recalculatingEmployeeId, setRecalculatingEmployeeId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const data = await payrollApi.getPayrollRunDetail(id);
        setDetail(data);
      } catch {
        toast.error('Failed to load payroll run details');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [id]);

  useEffect(() => {
    payrollApi
      .getModuleConfiguration()
      .then((config) => setBankAdviceEnabled(Boolean(config?.enableBankAdviceExport)))
      .catch(() => setBankAdviceEnabled(false));
  }, []);

  const reloadDetail = async () => {
    if (!id) return;
    const data = await payrollApi.getPayrollRunDetail(id);
    setDetail(data);
  };

  const getApiErrorMessage = (error: any, fallback: string) =>
    error?.response?.data?.message || error?.response?.data || fallback;

  const handleWorkflow = async (
    actionKey: string,
    action: () => Promise<void>,
    successMessage: string,
    fallbackError: string
  ) => {
    if (workflowAction) return;
    setWorkflowAction(actionKey);
    try {
      await action();
      toast.success(successMessage);
      await reloadDetail();
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, fallbackError));
    } finally {
      setWorkflowAction(null);
    }
  };

  const handleSaveAdjustment = async () => {
    if (!id || !adjustmentEmployee) return;
    const amount = Number(adjustmentAmount);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error('Enter a valid adjustment amount');
      return;
    }
    setSavingAdjustment(true);
    try {
      await payrollApi.addPayrollAdjustment(id, adjustmentEmployee.employeeId, {
        adjustmentType,
        componentName: PayrollAdjustmentTypeLabels[adjustmentType],
        amount,
        remarks: adjustmentRemarks || null,
      });
      toast.success('Adjustment saved and recalculated');
      setAdjustmentEmployee(null);
      setAdjustmentAmount('');
      setAdjustmentRemarks('');
      await reloadDetail();
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Failed to save adjustment'));
    } finally {
      setSavingAdjustment(false);
    }
  };

  const handleRecalculateEmployee = async (employeeId: string) => {
    if (!id || recalculatingEmployeeId) return;
    setRecalculatingEmployeeId(employeeId);
    try {
      await payrollApi.recalculatePayrollEmployee(id, employeeId);
      toast.success('Employee recalculated');
      await reloadDetail();
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Failed to recalculate employee'));
    } finally {
      setRecalculatingEmployeeId(null);
    }
  };

  const handleExport = async () => {
    if (!id || !detail || exporting) return;
    setExporting(true);
    try {
      const blob = await payrollApi.exportPayrollRun(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payroll-run-${detail.year}-${String(detail.month).padStart(2, '0')}.xls`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export payroll run');
    } finally {
      setExporting(false);
    }
  };

  const handleExportBankAdvice = async () => {
    if (!id || !detail) return;
    setDownloadingBankAdvice(true);
    try {
      const blob = await payrollApi.exportBankAdvice(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bank-advice-${detail.year}-${String(detail.month).padStart(2, '0')}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Bank advice exported');
    } catch (error: any) {
      let message = 'Failed to export bank advice';
      if (error?.response?.data instanceof Blob) {
        try {
          message = JSON.parse(await error.response.data.text())?.message || message;
        } catch {
          /* keep default message */
        }
      }
      toast.error(message);
    } finally {
      setDownloadingBankAdvice(false);
    }
  };

  const renderBreakdownTooltip = (title: string, components: PayrollComponentDetailDto[]) => (
    <Box sx={{ minWidth: 240, p: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {title}
      </Typography>
      {components.length === 0 ? (
        <Typography variant="body2">No components</Typography>
      ) : (
        components.map((component) => (
          <Stack
            key={`${title}-${component.componentId}`}
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{ py: 0.2 }}
          >
            <Typography variant="body2">{component.componentName}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatMoney(component.amount)}
            </Typography>
          </Stack>
        ))
      )}
    </Box>
  );

  // Every adjustment made on this row, including the Overtime ones that the total leaves to the
  // OT Amount column, so the figure next to it can always be explained.
  const renderAdjustmentTooltip = (adjustments: PayrollAdjustmentDto[]) => (
    <Box sx={{ minWidth: 240, p: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Adjustments
      </Typography>
      {adjustments.length === 0 ? (
        <Typography variant="body2">No adjustments</Typography>
      ) : (
        adjustments.map((adjustment) => (
          <Stack
            key={adjustment.id}
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{ py: 0.2 }}
          >
            <Typography variant="body2">
              {adjustment.componentName || PayrollAdjustmentTypeLabels[adjustment.adjustmentType]}
              {isOvertimeAdjustment(adjustment.adjustmentType) ? ' (shown in OT Amount)' : ''}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatMoney(adjustmentSignedAmount(adjustment))}
            </Typography>
          </Stack>
        ))
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!detail) return null;
  const isEditable = detail.status === PayrollRunStatus.Draft || detail.status === PayrollRunStatus.Review;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Tooltip title="Back to Payroll Runs">
          <IconButton onClick={() => navigate('/payroll/runs')}>
            <BackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h5" fontWeight={700}>
          Payroll Run - {getRunPeriodLabel(detail)}
        </Typography>
        <Chip
          label={PayCycleLabels[detail.payCycle ?? PayCycle.Monthly]}
          color="info"
          size="small"
        />
        <Chip
          label={PayrollRunStatusLabels[detail.status]}
          color={PayrollRunStatusColors[detail.status]}
          size="small"
        />
        <Box sx={{ flexGrow: 1 }} />
        {bankAdviceEnabled && (
          <Button
            variant="outlined"
            startIcon={downloadingBankAdvice ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
            onClick={handleExportBankAdvice}
            disabled={downloadingBankAdvice || detail.employeeDetails.length === 0}
          >
            {downloadingBankAdvice ? 'Preparing...' : 'Bank Advice'}
          </Button>
        )}
        <Tooltip title="Export payroll register">
          <span>
            <IconButton onClick={handleExport} disabled={exporting}>
              {exporting ? <CircularProgress size={22} color="inherit" /> : <DownloadIcon />}
            </IconButton>
          </span>
        </Tooltip>
        {isEditable && (
          <Button
            variant="outlined"
            disabled={Boolean(workflowAction)}
            startIcon={workflowAction === 'recalculate' ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
            onClick={() => id && handleWorkflow('recalculate', () => payrollApi.recalculatePayrollRun(id), 'Payroll recalculated', 'Failed to recalculate payroll')}
          >
            {workflowAction === 'recalculate' ? 'Recalculating...' : 'Recalculate All'}
          </Button>
        )}
        {detail.status === PayrollRunStatus.Draft && (
          <Button
            variant="contained"
            color="primary"
            disabled={Boolean(workflowAction)}
            startIcon={workflowAction === 'submit' ? <CircularProgress size={18} color="inherit" /> : undefined}
            onClick={() => id && handleWorkflow('submit', () => payrollApi.submitPayrollForReview(id), 'Submitted for review', 'Failed to submit payroll')}
          >
            {workflowAction === 'submit' ? 'Submitting...' : 'Submit for Review'}
          </Button>
        )}
        {detail.status === PayrollRunStatus.Review && (
          <Button
            variant="contained"
            color="primary"
            disabled={Boolean(workflowAction)}
            startIcon={workflowAction === 'approve' ? <CircularProgress size={18} color="inherit" /> : undefined}
            onClick={() => id && handleWorkflow('approve', () => payrollApi.approvePayrollRun(id), 'Payroll approved', 'Failed to approve payroll')}
          >
            {workflowAction === 'approve' ? 'Approving...' : 'Approve Payroll'}
          </Button>
        )}
        {detail.status === PayrollRunStatus.Approved && (
          <Button
            variant="contained"
            color="primary"
            disabled={Boolean(workflowAction)}
            startIcon={workflowAction === 'process' ? <CircularProgress size={18} color="inherit" /> : undefined}
            onClick={() => id && handleWorkflow('process', () => payrollApi.processPayrollRun(id), 'Payroll processed and locked', 'Failed to process payroll run')}
          >
            {workflowAction === 'process' ? 'Processing...' : 'Process Payroll'}
          </Button>
        )}
        {detail.status === PayrollRunStatus.Processed && (
          <Button
            variant="contained"
            color="success"
            disabled={Boolean(workflowAction)}
            startIcon={workflowAction === 'markPaid' ? <CircularProgress size={18} color="inherit" /> : undefined}
            onClick={() => id && handleWorkflow('markPaid', () => payrollApi.markPayrollRunPaid(id), 'Payroll marked as paid', 'Failed to mark payroll paid')}
          >
            {workflowAction === 'markPaid' ? 'Marking as Paid...' : 'Mark as Paid'}
          </Button>
        )}
      </Stack>

      <Paper>
        <TableContainer
          sx={{
            maxHeight: 'calc(100vh - 220px)',
            overflow: 'auto',
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: 'background.paper',
                  boxShadow: 'inset 0 -1px 0',
                  color: 'text.primary',
                  zIndex: 2,
                },
              }}
            >
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Code</TableCell>
                <TableCell align="right">Calendar Days</TableCell>
                <TableCell align="right">Working Days</TableCell>
                <TableCell align="right">Weekly Off</TableCell>
                <TableCell align="right">Holiday</TableCell>
                <TableCell align="right">Total Present Day</TableCell>
                <TableCell align="right">Full Day</TableCell>
                <TableCell align="right">Half Day</TableCell>
                <TableCell align="right">Paid Leave</TableCell>
                <TableCell align="right">Comp Off Used</TableCell>
                <TableCell align="right">Payable Days</TableCell>
                <TableCell align="right">LWP</TableCell>
                <TableCell align="right">Absent</TableCell>
                <TableCell align="right">LOP</TableCell>
                <TableCell align="right">Late Mark Ded.</TableCell>
                <TableCell align="right">Loan/Advance Ded.</TableCell>
                <TableCell align="right">Gross Salary</TableCell>
                <TableCell align="right">Earnings</TableCell>
                <TableCell align="right">Deductions</TableCell>
                <TableCell align="right">OT Hours</TableCell>
                <TableCell align="right">OT Amount</TableCell>
                <TableCell align="right">Adjustments</TableCell>
                <TableCell align="right">Net Salary</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.employeeDetails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={25} align="center">No employee details</TableCell>
                </TableRow>
              ) : (
                detail.employeeDetails.map((emp) => {
                  const deductions = (emp.componentDetails || [])
                    .filter((component) => deductionComponentTypes.includes(component.type));
                  const earnings = (emp.componentDetails || [])
                    .filter((component) => !deductionComponentTypes.includes(component.type));
                  const adjustments = emp.adjustments || [];
                  const adjustmentTotal = netAdjustmentTotal(adjustments);

                  return (
                    <TableRow key={emp.id}>
                      <TableCell>{emp.employeeName}</TableCell>
                      <TableCell>{emp.employeeCode}</TableCell>
                      <TableCell align="right">{formatDays(emp.totalCalendarDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.scheduledWorkingDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.weeklyOffDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.paidHolidayDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.presentDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.fullDayDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.halfDayDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.paidLeaveDays ?? emp.leaveDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.compOffLeaveDays ?? 0)}</TableCell>
                      <TableCell align="right">{formatDays(emp.payableDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.unpaidLeaveDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.absentDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.lopDays)}</TableCell>
                      <TableCell align="right">
                        {emp.lateMarkDeductionAmount > 0 ? (
                          <Tooltip
                            arrow
                            placement="left-start"
                            title={`${emp.lateMarkCount} late mark(s) this period — ${formatDays(emp.lateMarkDeductionDays)} day(s) deducted in total`}
                          >
                            <span>{formatMoney(emp.lateMarkDeductionAmount)}</span>
                          </Tooltip>
                        ) : (
                          formatMoney(0)
                        )}
                      </TableCell>
                      <TableCell align="right">{formatMoney(emp.loanRecoveryAmount)}</TableCell>
                      <TableCell align="right">{formatMoney(emp.grossSalary)}</TableCell>
                      <TableCell align="right">
                        <Tooltip arrow placement="left-start" title={renderBreakdownTooltip('Earnings', earnings)}>
                          <span>{formatMoney(emp.totalEarnings)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip arrow placement="left-start" title={renderBreakdownTooltip('Deductions', deductions)}>
                          <span>{formatMoney(emp.totalDeductions)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{formatDays(emp.overtimeHours)}</TableCell>
                      <TableCell align="right">{formatMoney(emp.overtimeAmount)}</TableCell>
                      <TableCell align="right">
                        <Tooltip arrow placement="left-start" title={renderAdjustmentTooltip(adjustments)}>
                          <span>{formatMoney(adjustmentTotal)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{formatMoney(emp.netSalary)}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Add Adjustment">
                            <span>
                              <IconButton
                                size="small"
                                disabled={!isEditable || Boolean(recalculatingEmployeeId) || Boolean(workflowAction)}
                                onClick={() => setAdjustmentEmployee(emp)}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Recalculate">
                            <span>
                              <IconButton
                                size="small"
                                disabled={!isEditable || Boolean(recalculatingEmployeeId) || Boolean(workflowAction)}
                                onClick={() => handleRecalculateEmployee(emp.employeeId)}
                              >
                                {recalculatingEmployeeId === emp.employeeId
                                  ? <CircularProgress size={16} color="inherit" />
                                  : <CalculateIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Paper sx={{ mt: 3, p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <HistoryIcon fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>Audit Log</Typography>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        {(detail.auditLogs || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">No audit entries yet</Typography>
        ) : (
          <Stack spacing={1}>
            {detail.auditLogs.slice(0, 8).map((log) => (
              <Box key={log.id}>
                <Typography variant="body2" fontWeight={600}>
                  {log.action}{log.employeeName ? ` - ${log.employeeName}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(log.createdAt).toLocaleString()} {log.newValue ? `| ${log.newValue}` : ''}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <Dialog open={Boolean(adjustmentEmployee)} onClose={() => setAdjustmentEmployee(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Adjustment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Employee"
              value={adjustmentEmployee?.employeeName ?? ''}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              select
              label="Adjustment Type"
              value={adjustmentType}
              onChange={(event) => setAdjustmentType(Number(event.target.value) as PayrollAdjustmentType)}
              fullWidth
            >
              {Object.entries(PayrollAdjustmentTypeLabels).map(([value, label]) => (
                <MenuItem key={value} value={Number(value)}>{label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Amount"
              type="number"
              value={adjustmentAmount}
              onChange={(event) => setAdjustmentAmount(event.target.value)}
              fullWidth
            />
            <TextField
              label="Remarks"
              value={adjustmentRemarks}
              onChange={(event) => setAdjustmentRemarks(event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustmentEmployee(null)} disabled={savingAdjustment}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveAdjustment}
            disabled={savingAdjustment}
            startIcon={savingAdjustment ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {savingAdjustment ? 'Saving...' : 'Save & Recalculate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollRunDetailPage;
